import { CRYPTO_CURRENCIES, FIAT_CURRENCIES, TRADING_CONFIG } from './constants';

/**
 * Format number with specified decimal places
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places
 * @param {boolean} removeTrailingZeros - Whether to remove trailing zeros
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 2, removeTrailingZeros = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const formatted = Number(value).toFixed(decimals);
  
  if (removeTrailingZeros) {
    return parseFloat(formatted).toString();
  }
  
  return formatted;
};

/**
 * Format currency value with symbol
 * @param {number} value - The value to format
 * @param {string} currency - Currency code (USD, EUR, etc.)
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'USD', options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true,
    locale = 'en-US'
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? `$0.00` : '0.00';
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: currency,
      minimumFractionDigits,
      maximumFractionDigits,
    });

    return formatter.format(value);
  } catch (error) {
    // Fallback formatting
    const fiatCurrency = FIAT_CURRENCIES.find(c => c.code === currency);
    const symbol = fiatCurrency ? fiatCurrency.symbol : '$';
    return showSymbol ? `${symbol}${formatNumber(value, minimumFractionDigits)}` : formatNumber(value, minimumFractionDigits);
  }
};

/**
 * Format cryptocurrency amount
 * @param {number} value - The amount to format
 * @param {string} symbol - Crypto symbol (BTC, ETH, etc.)
 * @param {object} options - Formatting options
 * @returns {string} Formatted crypto amount
 */
export const formatCrypto = (value, symbol = 'BTC', options = {}) => {
  const {
    showSymbol = true,
    maxDecimals = 8,
    minDecimals = 2,
    removeTrailingZeros = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? `0 ${symbol}` : '0';
  }

  const crypto = CRYPTO_CURRENCIES.find(c => c.symbol === symbol);
  const decimals = crypto ? crypto.decimals : maxDecimals;
  
  // Use appropriate decimal places based on value size
  let decimalPlaces = decimals;
  if (value >= 1) {
    decimalPlaces = Math.min(minDecimals, decimals);
  } else if (value >= 0.01) {
    decimalPlaces = Math.min(4, decimals);
  } else if (value >= 0.0001) {
    decimalPlaces = Math.min(6, decimals);
  }

  const formatted = formatNumber(value, decimalPlaces, removeTrailingZeros);
  
  return showSymbol ? `${formatted} ${symbol}` : formatted;
};

/**
 * Format percentage value
 * @param {number} value - The percentage value (0.05 = 5%)
 * @param {object} options - Formatting options
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, options = {}) => {
  const {
    decimals = 2,
    showSign = false,
    showSymbol = true,
    multiply100 = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? '0.00%' : '0.00';
  }

  const percentage = multiply100 ? value * 100 : value;
  const sign = showSign && percentage > 0 ? '+' : '';
  const formatted = formatNumber(percentage, decimals);
  
  return showSymbol ? `${sign}${formatted}%` : `${sign}${formatted}`;
};

/**
 * Format price change with color indication
 * @param {number} value - The price change value
 * @param {number} previousValue - The previous value for comparison
 * @param {object} options - Formatting options
 * @returns {object} Formatted price change with metadata
 */
export const formatPriceChange = (value, previousValue = null, options = {}) => {
  const {
    currency = 'USD',
    showPercentage = true,
    showAbsolute = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return {
      absolute: formatCurrency(0, currency),
      percentage: formatPercentage(0),
      direction: 'neutral',
      className: 'text-gray-500'
    };
  }

  let direction = 'neutral';
  let className = 'text-gray-500';
  
  if (value > 0) {
    direction = 'up';
    className = 'text-emerald-500';
  } else if (value < 0) {
    direction = 'down';
    className = 'text-red-500';
  }

  const absolute = formatCurrency(Math.abs(value), currency, { showSymbol: true });
  const sign = value >= 0 ? '+' : '-';
  
  let percentage = '';
  if (showPercentage && previousValue && previousValue !== 0) {
    const percentChange = (value / previousValue);
    percentage = formatPercentage(Math.abs(percentChange), { showSign: false });
  }

  let display = '';
  if (showAbsolute && showPercentage && percentage) {
    display = `${sign}${absolute} (${sign}${percentage})`;
  } else if (showAbsolute) {
    display = `${sign}${absolute}`;
  } else if (showPercentage && percentage) {
    display = `${sign}${percentage}`;
  }

  return {
    absolute,
    percentage,
    display,
    direction,
    className,
    sign: value >= 0 ? '+' : '-'
  };
};

/**
 * Format trading volume
 * @param {number} value - The volume value
 * @param {string} symbol - The trading symbol
 * @returns {string} Formatted volume
 */
export const formatVolume = (value, symbol = '') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);
  
  if (absValue >= 1e9) {
    return `${formatNumber(value / 1e9, 2)}B ${symbol}`.trim();
  } else if (absValue >= 1e6) {
    return `${formatNumber(value / 1e6, 2)}M ${symbol}`.trim();
  } else if (absValue >= 1e3) {
    return `${formatNumber(value / 1e3, 2)}K ${symbol}`.trim();
  } else {
    return `${formatNumber(value, 2)} ${symbol}`.trim();
  }
};

/**
 * Format market cap
 * @param {number} value - The market cap value
 * @param {string} currency - Currency symbol
 * @returns {string} Formatted market cap
 */
export const formatMarketCap = (value, currency = 'USD') => {
  if (value === null || value === undefined || isNaN(value)) {
    return formatCurrency(0, currency);
  }

  const absValue = Math.abs(value);
  const currencySymbol = FIAT_CURRENCIES.find(c => c.code === currency)?.symbol || '$';
  
  if (absValue >= 1e12) {
    return `${currencySymbol}${formatNumber(value / 1e12, 2)}T`;
  } else if (absValue >= 1e9) {
    return `${currencySymbol}${formatNumber(value / 1e9, 2)}B`;
  } else if (absValue >= 1e6) {
    return `${currencySymbol}${formatNumber(value / 1e6, 2)}M`;
  } else if (absValue >= 1e3) {
    return `${currencySymbol}${formatNumber(value / 1e3, 2)}K`;
  } else {
    return formatCurrency(value, currency);
  }
};

/**
 * Format date and time
 * @param {Date|string|number} date - The date to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted date
 */
export const formatDate = (date, options = {}) => {
  const {
    format = 'datetime',
    locale = 'en-US',
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } = options;

  if (!date) return '';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  const baseOptions = { timeZone: timezone };

  switch (format) {
    case 'date':
      return dateObj.toLocaleDateString(locale, {
        ...baseOptions,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    
    case 'time':
      return dateObj.toLocaleTimeString(locale, {
        ...baseOptions,
        hour: '2-digit',
        minute: '2-digit'
      });
    
    case 'datetime':
      return dateObj.toLocaleString(locale, {
        ...baseOptions,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    
    case 'relative':
      return formatRelativeTime(dateObj);
    
    case 'short':
      return dateObj.toLocaleDateString(locale, {
        ...baseOptions,
        month: 'numeric',
        day: 'numeric'
      });
    
    default:
      return dateObj.toLocaleString(locale, baseOptions);
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {Date} date - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - new Date(date)) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  }
};

/**
 * Format order status
 * @param {string} status - Order status
 * @returns {object} Formatted status with styling
 */
export const formatOrderStatus = (status) => {
  const statusMap = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      icon: '⏳'
    },
    filled: {
      label: 'Filled',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      icon: '✅'
    },
    partially_filled: {
      label: 'Partially Filled',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      icon: '🔄'
    },
    canceled: {
      label: 'Canceled',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
      icon: '❌'
    },
    rejected: {
      label: 'Rejected',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      icon: '🚫'
    },
    expired: {
      label: 'Expired',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
      icon: '⏰'
    }
  };

  return statusMap[status] || statusMap.pending;
};

/**
 * Format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format address (truncate middle part)
 * @param {string} address - The address to format
 * @param {number} startChars - Number of characters to show at start
 * @param {number} endChars - Number of characters to show at end
 * @returns {string} Formatted address
 */
export const formatAddress = (address, startChars = 6, endChars = 4) => {
  if (!address || address.length <= startChars + endChars) {
    return address;
  }
  
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Format trading pair
 * @param {string} pair - Trading pair (e.g., "BTCUSD")
 * @param {string} separator - Separator character
 * @returns {string} Formatted trading pair
 */
export const formatTradingPair = (pair, separator = '/') => {
  if (!pair) return '';
  
  // Handle pairs that already have separator
  if (pair.includes('/') || pair.includes('-') || pair.includes('_')) {
    return pair.replace(/[-_]/g, separator);
  }
  
  // Try to split common crypto pairs
  const commonPairs = [
    'USDT', 'USDC', 'USD', 'EUR', 'GBP', 'JPY', 'BTC', 'ETH', 'BNB'
  ];
  
  for (const quote of commonPairs) {
    if (pair.endsWith(quote)) {
      const base = pair.slice(0, -quote.length);
      if (base.length > 0) {
        return `${base}${separator}${quote}`;
      }
    }
  }
  
  // Default fallback - try to split at 3-4 characters
  if (pair.length >= 6) {
    const midPoint = Math.floor(pair.length / 2);
    return `${pair.slice(0, midPoint)}${separator}${pair.slice(midPoint)}`;
  }
  
  return pair;
};

/**
 * Format price with appropriate precision
 * @param {number} price - The price value
 * @param {string} symbol - Trading symbol for context
 * @param {object} options - Formatting options
 * @returns {string} Formatted price
 */
export const formatPrice = (price, symbol = '', options = {}) => {
  const { currency = 'USD', showCurrency = true } = options;
  
  if (price === null || price === undefined || isNaN(price)) {
    return showCurrency ? formatCurrency(0, currency) : '0.00';
  }

  // Determine decimal places based on price magnitude
  let decimals = 2;
  if (price >= 1000) {
    decimals = 2;
  } else if (price >= 1) {
    decimals = 2;
  } else if (price >= 0.01) {
    decimals = 4;
  } else if (price >= 0.0001) {
    decimals = 6;
  } else {
    decimals = 8;
  }

  if (showCurrency) {
    return formatCurrency(price, currency, { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  }

  return formatNumber(price, decimals);
};

/**
 * Format balance with appropriate currency
 * @param {number} balance - Balance amount
 * @param {string} currency - Currency code
 * @param {object} options - Formatting options
 * @returns {string} Formatted balance
 */
export const formatBalance = (balance, currency = 'USD', options = {}) => {
  const { showZero = true, hideSmallAmounts = false } = options;
  
  if (balance === null || balance === undefined || isNaN(balance)) {
    return showZero ? formatCurrency(0, currency) : '';
  }

  if (hideSmallAmounts && Math.abs(balance) < 0.01) {
    return showZero ? formatCurrency(0, currency) : '';
  }

  // Check if it's a cryptocurrency
  const isCrypto = CRYPTO_CURRENCIES.some(c => c.symbol === currency);
  
  if (isCrypto) {
    return formatCrypto(balance, currency, { showSymbol: true });
  }

  return formatCurrency(balance, currency);
};

/**
 * Format trade amount for display
 * @param {number} amount - Trade amount
 * @param {string} symbol - Asset symbol
 * @param {string} type - 'buy' or 'sell'
 * @returns {string} Formatted trade amount
 */
export const formatTradeAmount = (amount, symbol, type = 'buy') => {
  if (!amount || isNaN(amount)) return '0';
  
  const isCrypto = CRYPTO_CURRENCIES.some(c => c.symbol === symbol);
  const prefix = type === 'buy' ? '+' : '-';
  
  if (isCrypto) {
    return `${prefix}${formatCrypto(Math.abs(amount), symbol)}`;
  }
  
  return `${prefix}${formatCurrency(Math.abs(amount), symbol)}`;
};

/**
 * Format P&L (Profit & Loss)
 * @param {number} pnl - P&L value
 * @param {string} currency - Currency code
 * @param {object} options - Formatting options
 * @returns {object} Formatted P&L with styling
 */
export const formatPnL = (pnl, currency = 'USD', options = {}) => {
  const { showPercentage = false, originalValue = null } = options;
  
  if (pnl === null || pnl === undefined || isNaN(pnl)) {
    return {
      value: formatCurrency(0, currency),
      percentage: '',
      className: 'text-gray-500',
      direction: 'neutral'
    };
  }

  const isPositive = pnl >= 0;
  const className = isPositive ? 'text-emerald-500' : 'text-red-500';
  const direction = isPositive ? 'up' : 'down';
  const sign = isPositive ? '+' : '';
  
  let formattedValue = `${sign}${formatCurrency(Math.abs(pnl), currency)}`;
  let percentage = '';
  
  if (showPercentage && originalValue && originalValue !== 0) {
    const percentChange = (pnl / originalValue) * 100;
    percentage = ` (${sign}${formatNumber(Math.abs(percentChange), 2)}%)`;
  }

  return {
    value: formattedValue,
    percentage,
    display: `${formattedValue}${percentage}`,
    className,
    direction,
    isPositive
  };
};

/**
 * Format order quantity
 * @param {number} quantity - Order quantity
 * @param {string} symbol - Asset symbol
 * @returns {string} Formatted quantity
 */
export const formatOrderQuantity = (quantity, symbol) => {
  if (!quantity || isNaN(quantity)) return '0';
  
  const isCrypto = CRYPTO_CURRENCIES.some(c => c.symbol === symbol);
  
  if (isCrypto) {
    return formatCrypto(quantity, symbol, { showSymbol: false });
  }
  
  return formatNumber(quantity, 2);
};

/**
 * Sanitize and validate input for formatting
 * @param {any} value - Value to sanitize
 * @returns {number|null} Sanitized number or null
 */
export const sanitizeNumericInput = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  // Convert string to number
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);
  
  return isNaN(numValue) ? null : numValue;
};

/**
 * Format compact number (e.g., 1.2K, 3.4M)
 * @param {number} value - Number to format
 * @param {number} decimals - Decimal places
 * @returns {string} Compact formatted number
 */
export const formatCompactNumber = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (absValue >= 1e12) {
    return `${sign}${formatNumber(absValue / 1e12, decimals)}T`;
  } else if (absValue >= 1e9) {
    return `${sign}${formatNumber(absValue / 1e9, decimals)}B`;
  } else if (absValue >= 1e6) {
    return `${sign}${formatNumber(absValue / 1e6, decimals)}M`;
  } else if (absValue >= 1e3) {
    return `${sign}${formatNumber(absValue / 1e3, decimals)}K`;
  } else {
    return `${sign}${formatNumber(absValue, decimals)}`;
  }
};